/**
 * The pieces the Overview card and the Capital Stack tab draw the
 * capitalization with: one horizontal stack bar, the tranche rows that name
 * it, the leverage figures with their state, and — while the dossier is
 * unlocked — the stated inputs those figures are computed from, as inputs.
 *
 * One bar, not five charts. A capitalization is a part-to-whole of a handful
 * of ordered layers, so it is a single stacked bar: each tranche's width is its
 * share, the boundaries are the attachment points a lender quotes ("Senior
 * 0–75.0% LTC"), and the rows beside it carry every name and dollar so the
 * color is never the only identity. The equity the stack closes with, when no
 * document states it, is drawn hatched and labelled Implied.
 */
import type { CSSProperties, ReactNode } from 'react'

import { FIELD_STATE_TOKENS } from '@/components/fields/fieldState'
import { formatAmount } from './format'
import { EditableSlot } from './ui'
import { useDossierEdit } from './editContext'
import { isWritableField } from './editableFields'
import { EditableValue } from './EditableValue'
import { ValueStateChip } from './valueStateChip'
import type { FieldSourceRef } from './workspace/types'
import { attachmentRange, type CapitalInput, type Capitalization, type LeverageFigure } from './leverageModel'
import type { StackLayer } from './capitalStackModel'

/** Attachment points print with one decimal, the leverage convention. */
function point(value: number): string {
  return `${value.toFixed(1)}%`
}

/** The hatch an implied tranche is drawn with: the tranche's own color at
 *  45°, over the surface, so it reads as the same family but not a stated
 *  figure. The texture is the state, not decoration. */
function hatch(color: string): CSSProperties {
  return {
    backgroundImage: `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 5px)`,
    boxShadow: `inset 0 0 0 1px ${color}`,
  }
}

/**
 * The capitalization as one stacked bar.
 *
 * Segments are separated by a 2px gap in the surface color rather than a
 * stroke; each is focusable and names itself (tranche, dollars, share,
 * attachment) on hover and to assistive tech. A share label sits inside a
 * segment only when the segment is wide enough to hold it — otherwise the
 * rows beside the bar carry it.
 *
 * `size="hero"` is the Capital Stack tab's bar: it is the first thing the
 * panel draws and it takes the height the panel has to give (26px at the
 * least, 150px at the most), so a tall window makes the bar taller instead of
 * leaving the panel empty under its table. Tall enough, a segment carries its
 * dollars under its share; the label never says anything the row beside it
 * does not.
 */
export function CapitalizationBar({
  capitalization,
  size = 'compact',
}: {
  capitalization: Capitalization
  size?: 'compact' | 'full' | 'hero'
}) {
  const { segments } = capitalization
  const hero = size === 'hero'
  const height = hero ? 'min-h-[26px] flex-1' : size === 'full' ? 'h-6' : 'h-4'
  const insideMin = hero ? 6 : size === 'full' ? 9 : 16
  // Debt boundaries are the attachment points worth printing under the bar;
  // the last debt boundary is the deal's leverage. Labels that would collide
  // (closer than 9 points) are dropped — the rows still state them.
  const shownTicks: number[] = [0]
  for (const segment of segments) {
    if (!segment.isDebt || segment.detach >= 99.95) continue
    if (segment.detach - shownTicks[shownTicks.length - 1] >= 9) shownTicks.push(segment.detach)
  }
  // 100% always prints; an attachment point crowding it gives way.
  if (shownTicks.length > 1 && 100 - shownTicks[shownTicks.length - 1] < 9) shownTicks.pop()
  shownTicks.push(100)
  return (
    <div
      data-capitalization-bar={size}
      className={hero ? 'flex min-h-[42px] flex-col' : undefined}
      style={hero ? { flex: '3 0 auto', maxHeight: 164 } : undefined}
    >
      <div
        role="list"
        aria-label={`Capitalization of ${formatAmount(capitalization.total)} (${capitalization.totalLabel.toLowerCase()})`}
        className={`flex w-full gap-[2px] ${height}`}
      >
        {segments.map((segment, index) => {
          const first = index === 0
          const last = index === segments.length - 1
          const describe = `${segment.label}${segment.isImplied ? ' (implied)' : ''}: ${formatAmount(segment.amount)}, ${point(segment.share)} of the total, ${attachmentRange(segment)}${capitalization.ratio ? ` ${capitalization.ratio}` : ''}`
          return (
            <div
              key={segment.id}
              role="listitem"
              tabIndex={0}
              aria-label={describe}
              title={describe}
              data-segment={segment.id}
              data-implied={segment.isImplied ? '' : undefined}
              className={`flex min-w-[3px] items-center justify-center overflow-hidden outline-none focus-visible:ring-1 focus-visible:ring-amber-200 ${first ? 'rounded-l-[3px]' : ''} ${last ? 'rounded-r-[3px]' : ''}`}
              style={{
                flexGrow: segment.amount,
                flexBasis: 0,
                ...(segment.isImplied ? hatch(segment.color) : { background: segment.color }),
              }}
            >
              {segment.share >= insideMin ? (
                <span
                  aria-hidden="true"
                  className={`flex flex-col items-center whitespace-nowrap px-1 font-mono font-semibold leading-tight ${size === 'compact' ? 'text-[8.5px]' : 'text-[10px]'} ${segment.isImplied ? 'rounded-sm bg-black/75 text-stone-100' : ''}`}
                  style={segment.isImplied ? undefined : { color: segment.ink }}
                >
                  <span>{size !== 'compact' && segment.share >= 22 ? `${segment.label} · ${point(segment.share)}` : point(segment.share)}</span>
                  {hero ? (
                    <span data-segment-amount="" className="text-[9px] font-medium opacity-80">
                      {formatAmount(segment.amount)}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </div>
          )
        })}
      </div>
      <div aria-hidden="true" className="relative mt-0.5 h-3 shrink-0 font-mono text-[8px] text-stone-500">
        {shownTicks.map((tick) => (
          <span
            key={tick}
            className="absolute top-0 whitespace-nowrap"
            style={
              tick === 0
                ? { left: 0 }
                : tick === 100
                  ? { right: 0 }
                  : { left: `${tick}%`, transform: 'translateX(-50%)' }
            }
          >
            {tick === 0 ? '0%' : tick === 100 ? '100%' : `${point(tick)}${capitalization.ratio ? ` ${capitalization.ratio}` : ''}`}
          </span>
        ))}
      </div>
    </div>
  )
}

/** Whose statement a figure is: the sponsor's own model, said on the value,
 *  in the Overview's Sponsor-chip treatment (OverviewColumns' AttributionBadge),
 *  so a sponsor's purchase price never reads as the firm's. */
export function SponsorMark({ source }: { source: FieldSourceRef | null | undefined }) {
  if (!source || source.state !== 'stated' || source.attribution !== 'sponsor') return null
  const where = source.cellRef ?? source.label
  return (
    <span
      data-sponsor-mark=""
      title={`From the sponsor's own underwriting model${where ? ` — ${where}` : ''}. Not yet verified by this firm.`}
      className="rounded bg-amber-500/10 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-amber-400/90"
    >
      Sponsor
    </span>
  )
}

/** The marks a figure carries: Derived with its formula, Implied, or — for a
 *  stated field — its own value state and whose statement it is. */
export function FigureMarks({
  figure,
  sourceOf,
}: {
  figure: LeverageFigure
  sourceOf: (name: string | undefined) => FieldSourceRef | null
}) {
  if (figure.display === '—' || figure.display === '') return null
  if (figure.implied) {
    return (
      <span
        data-figure-implied=""
        title={figure.formula ? `Implied: ${figure.formula}. No document states it.` : 'Implied: no document states it.'}
        className="rounded bg-sky-400/10 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-sky-200"
      >
        Implied
      </span>
    )
  }
  if (figure.basis === 'derived' && !figure.field) {
    return (
      <span
        data-value-state="derived"
        title={`Derived: ${figure.formula ?? 'computed from the deal’s stated figures'}`}
        className={`rounded px-1 py-0.5 text-[7.5px] uppercase tracking-wide ring-1 ring-inset ${FIELD_STATE_TOKENS.derived.className}`}
      >
        {FIELD_STATE_TOKENS.derived.label}
      </span>
    )
  }
  const source = sourceOf(figure.field)
  return (
    <>
      <ValueStateChip source={source} />
      <SponsorMark source={source} />
    </>
  )
}

/** A tranche amount that is a catalogue field carries that field's state; a
 *  computed one (future funding, the implied equity) says how it was reached. */
export function AmountMarks({
  amountField,
  amountFormula,
  isImplied,
  sourceOf,
}: {
  amountField?: string
  amountFormula?: string
  isImplied?: boolean
  sourceOf: (name: string | undefined) => FieldSourceRef | null
}) {
  if (isImplied) {
    return (
      <span
        data-figure-implied=""
        title={`Implied: ${amountFormula ?? 'uses less debt'}. No document states it.`}
        className="rounded bg-sky-400/10 px-1 py-0.5 text-[7.5px] uppercase tracking-wide text-sky-200"
      >
        Implied
      </span>
    )
  }
  if (amountFormula) {
    return (
      <span
        data-value-state="derived"
        title={`Derived: ${amountFormula}`}
        className={`rounded px-1 py-0.5 text-[7.5px] uppercase tracking-wide ring-1 ring-inset ${FIELD_STATE_TOKENS.derived.className}`}
      >
        {FIELD_STATE_TOKENS.derived.label}
      </span>
    )
  }
  const source = sourceOf(amountField)
  return (
    <>
      <ValueStateChip source={source} />
      <SponsorMark source={source} />
    </>
  )
}

/** Where a field is already edited on the same screen, as a short place name
 *  ("Loan Overview"), or null where this surface is its one editor. */
export type EditedElsewhere = (field: string) => string | null

/**
 * A capital amount as the surface prints it — its marks, then the figure —
 * and, unlocked, edited the one way the dossier can (owner, 21 Oct: every
 * populated value editable in place; R1: an output is never typed over):
 *
 *   - the catalogue field it IS (a stated total equity, the purchase price
 *     a stack closes on): that field's input, in place;
 *   - a computed amount (future funding, the implied equity, a total that is
 *     the tranches summed): opens the inputs it is made of;
 *   - a field another panel on the same screen already edits: read as it
 *     does locked, naming where — one editor per field per screen;
 *   - a stored tranche no catalogue field holds: read as it does locked,
 *     with the reason on hover.
 *
 * Locked it draws exactly what the rows drew before: the marks, the amount.
 */
export function EditableAmount({
  id,
  label,
  display,
  amountField,
  amountFormula,
  amountInputs,
  isImplied,
  readOnlyReason,
  editedElsewhere,
  sourceOf,
  marks = true,
}: {
  /** A stable name for a computed amount's control (the segment id). */
  id: string
  /** What the amount is called here; the input's accessible name. */
  label: string
  display: string
  amountField?: string
  amountFormula?: string
  amountInputs?: string[]
  isImplied?: boolean
  readOnlyReason?: string
  editedElsewhere?: EditedElsewhere
  sourceOf: (name: string | undefined) => FieldSourceRef | null
  /** Draw the amount's state marks beside it (a total row draws its own). */
  marks?: boolean
}) {
  const drawn = marks ? (
    <AmountMarks amountField={amountField} amountFormula={amountFormula} isImplied={isImplied} sourceOf={sourceOf} />
  ) : null
  if (amountFormula !== undefined || isImplied) {
    return (
      <EditableValue
        name={id}
        label={label}
        display={display}
        marks={drawn}
        marksFirst
        computed={amountFormula ?? ''}
        inputs={amountInputs ?? []}
      />
    )
  }
  if (amountField) {
    const where = editedElsewhere?.(amountField) ?? null
    return (
      <EditableValue
        name={amountField}
        label={label}
        display={display}
        marks={drawn}
        marksFirst
        fields={where ? [] : undefined}
        readOnlyReason={where ? `Edited in ${where} on this screen — one editor per field.` : undefined}
      />
    )
  }
  return (
    <EditableValue
      name={id}
      label={label}
      display={display}
      marks={drawn}
      marksFirst
      fields={[]}
      readOnlyReason={readOnlyReason}
    />
  )
}

/** The request tag on the tranche the deal asks for. */
export function RequestTag() {
  return (
    <span className="rounded border border-amber-300/30 bg-amber-300/[0.08] px-1 font-mono text-[7.5px] uppercase tracking-wider text-amber-200">
      Request
    </span>
  )
}

/** A swatch for a tranche row: its fill, or its hatch when implied. */
export function Swatch({ color, implied }: { color: string; implied?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
      style={implied ? hatch(color) : { background: color }}
    />
  )
}

/** The tranches a deal records when no total is known: dollars, no shares. */
export function UnsharedTranches({
  tranches,
  sourceOf,
  editedElsewhere,
}: {
  tranches: StackLayer[]
  sourceOf: (name: string | undefined) => FieldSourceRef | null
  editedElsewhere?: EditedElsewhere
}) {
  return (
    <ul className="space-y-0.5 text-[10px]">
      {tranches.map((layer) => (
        <li key={layer.id} className="flex items-center gap-1.5">
          <Swatch color="#3987e5" />
          <span className="min-w-0 flex-1 truncate text-stone-300" title={layer.position}>{layer.position}</span>
          {layer.isRequested ? <RequestTag /> : null}
          <span className="inline-flex shrink-0 items-center gap-1 font-mono text-stone-100">
            <EditableAmount
              id={layer.id}
              label={layer.position}
              display={formatAmount(layer.amount)}
              amountField={layer.amountField}
              amountFormula={layer.amountDerived}
              amountInputs={layer.amountInputs}
              isImplied={layer.isImplied}
              readOnlyReason={layer.readOnlyReason}
              editedElsewhere={editedElsewhere}
              sourceOf={sourceOf}
            />
          </span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The stated inputs behind a panel's figures, as inputs, while the dossier is
 * unlocked. Every derived figure above is computed from these, so a person
 * corrects the input and the figure follows — the derived figures themselves
 * are never input boxes (R1: people resolve inputs, never outputs).
 *
 * The existing editable primitive, unchanged: `EditableSlot` in its row
 * layout, exactly as the Economics Financing Summary uses it. Locked, nothing
 * renders here: the figures above already print these values.
 *
 * Only an input a save can write is offered. One the catalogue does not
 * define (`closing_costs`, `capex` on the shipped catalogue) drew a dead
 * "Closing Costs —" row, read-only, under a heading that says Inputs.
 */
export function InputsBlock({ inputs, note }: { inputs: CapitalInput[]; note?: ReactNode }) {
  const { unlocked, defs } = useDossierEdit()
  const offered = inputs.filter((input) => isWritableField(input.name, defs))
  if (!unlocked || offered.length === 0) return null
  return (
    <div data-capital-inputs="" className="mt-2 space-y-1 border-t border-amber-300/15 pt-1.5">
      <div className="font-mono text-[8px] uppercase tracking-wider text-amber-300/80">Inputs</div>
      {note ? <p className="text-[9px] leading-snug text-stone-500">{note}</p> : null}
      {offered.map((input) => (
        <EditableSlot
          key={input.name}
          name={input.name}
          label={input.label}
          display="—"
          kind={input.kind}
          layout="row"
        />
      ))}
    </div>
  )
}
